import { useMutation } from '@tanstack/react-query';
// import { Brain } from 'lucide-react';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import applicationsService from '@/services/applications/applicationsService';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';

type SendBrochureForm = {
  name: string;
  email: string;
};

export default function Footer() {
  const { t } = useTranslation('landing');
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SendBrochureForm>({
    defaultValues: {
      name: '',
      email: '',
    },
  });

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const sendBrochureMutation = useMutation({
    mutationFn: (payload: SendBrochureForm) =>
      applicationsService.sendBrochure({
        name: payload.name.trim(),
        email: payload.email.trim(),
      }),
    onSuccess: (response) => {
      toast.success(response.message ?? t('footer.brochure.toast.success'));
      reset();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('footer.brochure.toast.error')));
    },
  });

  const handleSendBrochure = handleSubmit((values) => {
    sendBrochureMutation.mutate(values);
  });

  return (
    <footer className="bg-[#0B112B] text-slate-400 py-20 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">
          
          {/* Column 1: Logo & Social */}
          <div className="md:col-span-5 flex flex-col gap-6">
            <div className="flex items-center gap-3">
              {/* <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                <Brain className="w-5 h-5 text-white" />
              </div> */}
              <div className="font-extrabold tracking-[0.12em] text-sm flex items-center text-white">
                {t('footer.brand')}
              </div>
            </div>
            <p className="text-slate-400 text-sm font-light leading-relaxed max-w-xs">
              {t('footer.description')}
            </p>

          </div>

          {/* Column 2: PROGRAM */}
          <div className="md:col-span-3 flex flex-col gap-6">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider text-left">{t('footer.programTitle')}</h4>
            <ul className="space-y-4 text-sm list-none p-0 m-0">
              <li>
                <button onClick={() => scrollToSection("program")} className="bg-transparent border-0 cursor-pointer p-0 text-slate-400 hover:text-white transition-colors text-left whitespace-nowrap">
                  {t('footer.links.aiProgram')}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection("themes")} className="bg-transparent border-0 cursor-pointer p-0 text-slate-400 hover:text-white transition-colors text-left whitespace-nowrap">
                  {t('footer.links.themes')}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection("journey")} className="bg-transparent border-0 cursor-pointer p-0 text-slate-400 hover:text-white transition-colors text-left whitespace-nowrap">
                  {t('footer.links.journey')}
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection("journey")} className="bg-transparent border-0 cursor-pointer p-0 text-slate-400 hover:text-white transition-colors text-left whitespace-nowrap">
                  {t('footer.links.curriculum')}
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: COMPANY */}
          {/* <div className="md:col-span-2 flex flex-col gap-6">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider text-left">COMPANY</h4>
            <ul className="space-y-4 text-sm list-none p-0 m-0">
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors no-underline whitespace-nowrap text-left">About</a></li>
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors no-underline whitespace-nowrap text-left">Team</a></li>
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors no-underline whitespace-nowrap text-left">Partners</a></li>
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors no-underline whitespace-nowrap text-left">Blog</a></li>
            </ul>
          </div> */}

          {/* Column 4: SUPPORT */}
          {/* <div className="md:col-span-2 flex flex-col gap-6">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider text-left">SUPPORT</h4>
            <ul className="space-y-4 text-sm list-none p-0 m-0">
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors no-underline whitespace-nowrap text-left">Contact Us</a></li>
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors no-underline whitespace-nowrap text-left">FAQs</a></li>
              <li><a href="#" className="text-slate-400 hover:text-white transition-colors no-underline whitespace-nowrap text-left">Book a Call</a></li>
            </ul>
          </div> */}

          {/* Column 5: DOWNLOAD BROCHURE */}
          <div className="md:col-span-4 flex flex-col gap-4">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider text-left">{t('footer.brochure.title')}</h4>
            <form onSubmit={handleSendBrochure} className="flex flex-col gap-3">
              <input
                type="text"
                placeholder={t('footer.brochure.namePlaceholder')}
                disabled={sendBrochureMutation.isPending}
                aria-invalid={errors.name ? 'true' : 'false'}
                {...register('name', {
                  required: t('footer.brochure.validation.nameRequired'),
                })}
                className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-primary/60 transition-all disabled:cursor-not-allowed disabled:opacity-70"
              />
              {errors.name ? (
                <p className="px-2 text-xs text-red-300">{errors.name.message}</p>
              ) : null}
              <input
                type="email"
                placeholder={t('footer.brochure.emailPlaceholder')}
                disabled={sendBrochureMutation.isPending}
                aria-invalid={errors.email ? 'true' : 'false'}
                {...register('email', {
                  required: t('footer.brochure.validation.emailRequired'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('footer.brochure.validation.emailInvalid'),
                  },
                })}
                className="w-full bg-white/5 border border-white/10 rounded-full px-5 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-primary/60 transition-all disabled:cursor-not-allowed disabled:opacity-70"
              />
              {errors.email ? (
                <p className="px-2 text-xs text-red-300">{errors.email.message}</p>
              ) : null}
              <button
                type="submit"
                disabled={sendBrochureMutation.isPending}
                className="w-full bg-primary hover:bg-primary/95 text-white rounded-full py-3.5 text-sm font-semibold transition-all cursor-pointer border-0 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {sendBrochureMutation.isPending
                  ? t('footer.brochure.buttonSending')
                  : t('footer.brochure.button')}
              </button>
            </form>
          </div>

        </div>

        {/* Legal / Copyright Bottom Block */}
        <div className="border-t border-white/5 pt-8 mt-16 flex flex-col sm:flex-row items-center justify-between gap-6 text-xs font-light text-slate-500">
          <span>{t('footer.copyright')}</span>
          <div className="flex flex-wrap gap-6">
            <a href="#" className="hover:text-slate-350 transition-colors no-underline text-slate-500">{t('footer.legal.terms')}</a>
            <a href="#" className="hover:text-slate-350 transition-colors no-underline text-slate-500">{t('footer.legal.privacy')}</a>
            <a href="#" className="hover:text-slate-350 transition-colors no-underline text-slate-500">{t('footer.legal.refund')}</a>
          </div>
        </div>

      </div>
    </footer>
  );
}
